import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { effectivePrice, useCatalogue, useDebounced } from '@/lib/catalogue';
import {
  useAjouterAuPanier,
  useChangerQuantite,
  useModifierEntete,
  useOrder,
  useOrders,
  type OrderSummary,
} from '@/lib/orders';
import { formatMoney } from '@/lib/utils';

/**
 * Vue de supervision des commandes (admin). L'admin ne prend pas de commande : il
 * surveille et corrige. On liste donc TOUTES les commandes — brouillons compris —
 * avec la possibilité d'annuler celles qui traînent ou sont erronées.
 *
 * `GET /api/orders` renvoie déjà tout pour un admin ; l'annulation passe par la
 * route `cancel` (statut → CANCELLED), qui autorise l'admin sur n'importe quelle
 * commande.
 */

type Filtre = 'TOUTES' | 'DRAFT' | 'SUBMITTED' | 'CANCELLED';

const FILTRES: { value: Filtre; label: string }[] = [
  { value: 'TOUTES', label: 'Toutes' },
  { value: 'DRAFT', label: 'Brouillons' },
  { value: 'SUBMITTED', label: 'Soumises' },
  { value: 'CANCELLED', label: 'Annulées' },
];

const STATUT: Record<OrderSummary['status'], { label: string; cls: string }> = {
  DRAFT: { label: 'brouillon', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  SUBMITTED: { label: 'soumise', cls: 'bg-green-600/15 text-green-700 dark:text-green-400' },
  CANCELLED: { label: 'annulée', cls: 'bg-destructive/15 text-destructive' },
};

const LIMIT = 100;

export default function Orders() {
  const [filtre, setFiltre] = useState<Filtre>('TOUTES');
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, 250);
  const [page, setPage] = useState(0);
  const status = filtre === 'TOUTES' ? undefined : filtre;
  const { data, isPending, isError } = useOrders({
    q: debouncedQ,
    status,
    limit: LIMIT,
    offset: page * LIMIT,
  });
  const qc = useQueryClient();
  const [editId, setEditId] = useState<number | null>(null);

  // Retour à la première page quand la recherche ou le filtre de statut change.
  useEffect(() => setPage(0), [debouncedQ, filtre]);

  const annuler = useMutation({
    mutationFn: (id: number) => api(`/api/orders/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => api(`/api/orders/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });

  // La liste est déjà filtrée (recherche + statut) et paginée côté serveur.
  const commandes = data?.orders ?? [];
  const counts = data?.counts ?? { total: 0, DRAFT: 0, SUBMITTED: 0, CANCELLED: 0 };
  const total = data?.total ?? 0;

  function onAnnuler(o: OrderSummary) {
    const nom = o.number ?? 'ce brouillon';
    if (!confirm(`Annuler ${nom} — ${o.customerName} ? Elle passera en « annulée » et sortira des totaux.`)) {
      return;
    }
    annuler.mutate(o.id);
  }

  function onSupprimer(o: OrderSummary) {
    const nom = o.number ?? 'ce brouillon';
    if (
      !confirm(
        `Supprimer DÉFINITIVEMENT ${nom} — ${o.customerName} ? Cette action est irréversible.`,
      )
    ) {
      return;
    }
    supprimer.mutate(o.id);
  }

  if (isPending) return <p className="text-muted-foreground p-8 text-center">Chargement…</p>;
  if (isError) return <p className="text-destructive p-8 text-center">Commandes indisponibles.</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold">Commandes</h2>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher : client, n° de client ou n° de commande…"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="border-input bg-background h-10 w-80 rounded-lg border px-3 text-sm"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTRES.map((f) => {
          const n = f.value === 'TOUTES' ? counts.total : counts[f.value];
          return (
            <button
              key={f.value}
              onClick={() => setFiltre(f.value)}
              aria-pressed={filtre === f.value}
              className={`h-10 rounded-lg border px-3 text-sm font-medium ${
                filtre === f.value ? 'border-primary bg-primary/10' : 'border-input'
              }`}
            >
              {f.label}
              <span className="text-muted-foreground ml-1.5 text-xs">{n}</span>
            </button>
          );
        })}
      </div>

      {(annuler.isError || supprimer.isError) && (
        <p className="text-destructive text-sm font-medium">
          {((annuler.error ?? supprimer.error) as Error)?.message}
        </p>
      )}

      {commandes.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">Aucune commande dans ce filtre.</p>
        </div>
      ) : (
        <div className="bg-card overflow-x-auto rounded-xl border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-left">
                <th className="p-3 font-medium">Date</th>
                <th className="p-3 font-medium">Numéro</th>
                <th className="p-3 font-medium">Client</th>
                <th className="p-3 font-medium">Statut</th>
                <th className="p-3 text-right font-medium">Total</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {commandes.map((o) => {
                const s = STATUT[o.status];
                const annulEnCours = annuler.isPending && annuler.variables === o.id;
                const suppEnCours = supprimer.isPending && supprimer.variables === o.id;
                return (
                  <Fragment key={o.id}>
                    <tr className="border-t">
                      <td className="text-muted-foreground p-3 whitespace-nowrap tabular-nums">
                        {new Date(o.createdAt).toLocaleString('fr-CA', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </td>
                      <td className="p-3 font-mono text-xs">{o.number ?? '—'}</td>
                      <td className="p-3">
                        <div className="font-medium">{o.customerName}</div>
                        <div className="text-muted-foreground font-mono text-xs">{o.custNum}</div>
                        {o.signerName ? (
                          <div className="text-muted-foreground text-xs">Par : {o.signerName}</div>
                        ) : o.status === 'SUBMITTED' ? (
                          <div className="text-xs font-medium text-amber-600 dark:text-amber-400">
                            Sans nom
                          </div>
                        ) : null}
                      </td>
                      <td className="p-3">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${s.cls}`}>
                          {s.label}
                        </span>
                      </td>
                      <td className="p-3 text-right font-semibold tabular-nums whitespace-nowrap">
                        {formatMoney(o.total)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditId(editId === o.id ? null : o.id)}
                            className="border-input rounded-lg border px-3 py-1.5 text-sm font-medium"
                          >
                            {editId === o.id ? 'Fermer' : 'Modifier'}
                          </button>
                          {o.status === 'SUBMITTED' && (
                            <button
                              onClick={() => onAnnuler(o)}
                              disabled={annulEnCours}
                              className="border-input text-foreground rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                            >
                              {annulEnCours ? 'Annulation…' : 'Annuler'}
                            </button>
                          )}
                          {(o.status === 'DRAFT' || o.status === 'CANCELLED') && (
                            <button
                              onClick={() => onSupprimer(o)}
                              disabled={suppEnCours}
                              className="border-destructive/40 text-destructive rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
                            >
                              {suppEnCours ? 'Suppression…' : 'Supprimer'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {editId === o.id && <LigneEdition orderId={o.id} onFini={() => setEditId(null)} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination : accéder à TOUTES les commandes, pas seulement les 100 premières. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground text-xs">
          {total > 0
            ? `${page * LIMIT + 1}–${page * LIMIT + commandes.length} sur ${total} commande(s)`
            : '0 commande'}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="border-input rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
          >
            ← Précédentes
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * LIMIT >= total}
            className="border-input rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40"
          >
            Suivantes →
          </button>
        </div>
      </div>

      <p className="text-muted-foreground text-xs">
        Annuler une commande soumise la retire des totaux ; les brouillons et les commandes annulées
        peuvent être supprimés définitivement. « Modifier » corrige l'entête (Nom et prénom, PO,
        notes) sans toucher aux produits.
      </p>
    </div>
  );
}

/**
 * Édition complète d'une commande (admin), chargée à la demande : entête (Nom et
 * prénom, PO, notes) ET produits (quantités, retrait, ajout par recherche). Les
 * modifications de lignes sont enregistrées à chaque geste ; le statut et le numéro
 * ne changent pas (correction en place).
 */
function LigneEdition({ orderId, onFini }: { orderId: number; onFini: () => void }) {
  const { data, isPending } = useOrder(orderId);
  const o = data?.order;

  const modifier = useModifierEntete();
  const changer = useChangerQuantite(orderId);
  const ajouter = useAjouterAuPanier(orderId);

  const [form, setForm] = useState({ signerName: '', poNumber: '', notes: '' });
  const [init, setInit] = useState(false);
  useEffect(() => {
    if (o && !init) {
      setForm({ signerName: o.signerName ?? '', poNumber: o.poNumber ?? '', notes: o.notes ?? '' });
      setInit(true);
    }
  }, [o, init]);

  const [q, setQ] = useState('');
  const recherche = useDebounced(q, 250);
  const catalogue = useCatalogue(
    { q: recherche, lineCode: null, sort: 'part' },
    recherche.trim() !== '',
  );

  const cls = 'border-input bg-background h-10 w-full rounded-lg border px-3 text-sm';

  if (isPending || !o) {
    return (
      <tr className="bg-muted/30 border-t">
        <td colSpan={6} className="p-4">
          <p className="text-muted-foreground text-sm">Chargement…</p>
        </td>
      </tr>
    );
  }

  return (
    <tr className="bg-muted/30 border-t">
      <td colSpan={6} className="space-y-5 p-4">
        {/* --- Entête --- */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Entête</h4>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="space-y-1">
              <span className="block text-sm font-medium">Nom et prénom (signataire)</span>
              <input
                value={form.signerName}
                onChange={(e) => setForm({ ...form, signerName: e.target.value })}
                className={cls}
              />
            </label>
            <label className="space-y-1">
              <span className="block text-sm font-medium">Bon de commande (PO)</span>
              <input
                value={form.poNumber}
                onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                className={cls}
              />
            </label>
            <label className="space-y-1">
              <span className="block text-sm font-medium">Notes</span>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className={cls}
              />
            </label>
          </div>
          <button
            onClick={() => modifier.mutate({ id: orderId, ...form })}
            disabled={modifier.isPending}
            className="bg-primary text-primary-foreground h-10 rounded-lg px-4 text-sm font-semibold disabled:opacity-40"
          >
            {modifier.isPending ? 'Enregistrement…' : "Enregistrer l'entête"}
          </button>
          {modifier.isError && (
            <p className="text-destructive text-sm">{(modifier.error as Error).message}</p>
          )}
        </div>

        {/* --- Produits --- */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">Produits</h4>
          {o.lines.length === 0 ? (
            <p className="text-muted-foreground text-sm">Aucun produit.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {o.lines.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center gap-3 p-2">
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="font-mono text-xs font-semibold">{l.partNumber}</span>{' '}
                    {l.description}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => changer.mutate({ lineId: l.id, qty: l.qty - 1 })}
                      disabled={changer.isPending}
                      className="border-input h-8 w-8 rounded-lg border font-bold disabled:opacity-40"
                    >
                      −
                    </button>
                    <span className="w-8 text-center text-sm font-semibold tabular-nums">{l.qty}</span>
                    <button
                      onClick={() => changer.mutate({ lineId: l.id, qty: l.qty + 1 })}
                      disabled={changer.isPending}
                      className="border-input h-8 w-8 rounded-lg border font-bold disabled:opacity-40"
                    >
                      +
                    </button>
                  </div>
                  <span className="w-24 text-right text-sm font-semibold tabular-nums">
                    {formatMoney(l.lineTotal)}
                  </span>
                  <button
                    onClick={() => changer.mutate({ lineId: l.id, qty: 0 })}
                    disabled={changer.isPending}
                    className="border-destructive/40 text-destructive rounded-lg border px-2 py-1 text-xs font-medium disabled:opacity-40"
                  >
                    Retirer
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="text-right text-sm font-semibold">Total : {formatMoney(o.total)}</div>

          {/* Ajouter un produit par recherche. */}
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ajouter un produit — n° de pièce ou description…"
            className={cls}
          />
          {recherche.trim() !== '' && (
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border p-1">
              {catalogue.isPending ? (
                <p className="text-muted-foreground p-2 text-sm">Recherche…</p>
              ) : catalogue.products.length === 0 ? (
                <p className="text-muted-foreground p-2 text-sm">Aucun produit.</p>
              ) : (
                catalogue.products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => ajouter.mutate({ productId: p.id })}
                    disabled={ajouter.isPending}
                    className="hover:bg-accent flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm disabled:opacity-40"
                  >
                    <span className="bg-secondary text-secondary-foreground rounded px-1.5 py-0.5 font-mono text-[11px]">
                      {p.lineCode}
                    </span>
                    <span className="font-mono text-xs font-semibold">{p.partNumber}</span>
                    <span className="min-w-0 flex-1 truncate">{p.description}</span>
                    <span className="font-semibold tabular-nums">{formatMoney(effectivePrice(p))}</span>
                  </button>
                ))
              )}
            </div>
          )}
          {ajouter.isError && (
            <p className="text-destructive text-sm">{(ajouter.error as Error).message}</p>
          )}
        </div>

        <button
          onClick={onFini}
          className="border-input h-10 rounded-lg border px-4 text-sm font-medium"
        >
          Fermer
        </button>
      </td>
    </tr>
  );
}
